import { faAngleLeft } from "@fortawesome/free-solid-svg-icons/faAngleLeft";
import { faAngleRight } from "@fortawesome/free-solid-svg-icons/faAngleRight";
import { Steps } from "../../utils/local-antd";
import type { StepsProps } from "../../utils/local-antd";
import React, { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { Images } from "../../assets";
import i18n from "../../i18n";
import { Rounded } from "../../theme/Rounded";
import { Spacing } from "../../theme/Spacing";
import { AtiraIcon } from "../AtiraIcon";
import { Button } from "../Button";
import { Flex } from "../Flex";
import { AtiraImage } from "../Image";
import { StepProvider, useStepContext } from "./StepperContext";
import capitalize from "lodash/capitalize";
import { ThemeKeys, useTheme } from "../../ThemeContext";
import { Breakpoints } from "../../theme/Breakpoints";
import isFunction from "lodash/isFunction";
import { EntityIcons } from "../../utils/GlobalIcons";
import { AppLangs } from "../../model/shared/enum/AppLangs.enum";

const StyledSteps = styled(Steps)<{ orientation: StepsProps["orientation"] }>`
  width: ${({ orientation }) =>
    orientation === "horizontal" ? "100%" : "fit-content"};
  background-color: ${({ theme }) => theme.white};
  border-radius: ${({ orientation }) =>
    orientation === "horizontal" ? Rounded.md : Rounded.none};
  padding: ${({ orientation }) =>
    orientation === "horizontal" ? Spacing.s : Spacing.m};
  overflow-x: scroll;
  display: flex;
  gap: ${({ orientation }) =>
    orientation === "horizontal" ? Spacing.m : Spacing.xl};
  scrollbar-width: none;
  gap: 0;

  #stepper-step {
    padding: 0;
  }

  &.ant-steps-vertical {
    overflow-x: visible !important;
    padding-inline-start: 0 !important;
    position: sticky;
    top: 0;
    height: fit-content;
  }

  .ant-steps-item {
    white-space: nowrap !important;
    display: flex !important;
    overflow: visible;
    position: static !important;
    flex-shrink: 0;
  }

  .ant-steps-item-header {
    user-select: none;
  }

  .ant-steps-item-icon {
    background-color: ${({ theme }) => theme.sub} !important;
  }

  .ant-steps-item-active .ant-steps-item-icon {
    background-color: ${({ theme }) => theme.main} !important;
  }

  .ant-steps-item-container {
    display: flex !important;
    align-items: center !important;
    white-space: nowrap !important;
  }

  ${({ orientation }) =>
    orientation === "vertical"
      ? `
    .ant-steps-item-content {
      min-height: auto !important;
    }

    .ant-steps-item-tail::after {
      display: none !important;
    }
  `
      : ""}

  .ant-steps-item-title::after {
    width: 1rem !important;
    background-color: ${({ theme }) => theme.main} !important;
  }

  .anticon {
    height: 100%;
  }

  .ant-steps-item-section {
    &:hover {
      cursor: pointer;
      border-bottom: 1px solid ${({ theme }) => theme.main} !important;
    }
  }

  @media (min-width: ${Breakpoints.DESKTOP}) {
    gap: ${Spacing.m};
  }
`;

const Container = styled(Flex)<{ orientation?: StepsProps["orientation"] }>`
  width: 100%;
  flex-direction: ${({ orientation }) =>
    orientation === "horizontal" ? "column" : "row"};
  padding: ${({ orientation }) =>
    orientation === "vertical" ? Spacing.l : "0"};
  gap: ${({ orientation }) =>
    orientation === "vertical" ? Spacing.none : Spacing.m};
  flex: 1;

  ${({ orientation, theme }) =>
    orientation === "vertical"
      ? `
    background-color: ${theme.white};
    border-radius: ${Rounded.md};
    padding: 0;
  `
      : ""}
`;

const CreateButton = styled(Button)`
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${Spacing.s};
  width: 7rem;
`;

const SaveExitButton = styled(Button)`
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${Spacing.s};
  width: 10rem;
  margin-inline-end: ${Spacing.m};
`;

const NextStepButton = styled(Button)`
  margin: 0;

  & > div {
    flex-direction: row-reverse;
  }
`;

const StepsExtraWrapper = styled(Flex)`
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-inline-end: ${Spacing.m};
  position: fixed;
  bottom: ${Spacing.m};
  background-color: ${({ theme }) => theme.white};
  padding: ${Spacing.xs};
`;

interface AtiraStepperProps extends StepsProps {
  components: ReactNode[];
  items: StepsProps["items"];
  style?: React.CSSProperties;
  fixed?: boolean;
  createLoading?: boolean;
  onCreate?: VoidFunction;
  onEdit?: VoidFunction;
  createButtonTitle?: string;
  nextButtonDataTestId?: string;
  prevButtonDataTestId?: string;
  nextButtonLabel?: string;
  prevButtonLabel?: string;
  editMode?: boolean;
  stepsExtraComponent?: ReactNode;
}

const AtiraStepper: React.FC<AtiraStepperProps> = ({
  items,
  components,
  style,
  onCreate,
  createLoading = false,
  createButtonTitle,
  nextButtonDataTestId,
  prevButtonDataTestId,
  nextButtonLabel,
  prevButtonLabel,
  orientation,
  editMode,
  stepsExtraComponent,
}) => {
  const { t } = useTranslation();

  const {
    currentStep,
    onGoToStep,
    onNext,
    onPrev,
    validations,
    canSaveAndExit,
  } = useStepContext();

  const { selectedTheme } = useTheme();
  // fallback for older saved themes/saved themes
  const currentTheme =
    (selectedTheme as any) === "light" ? ThemeKeys.PURPLE : selectedTheme;
  const canGoNext = validations[currentStep];
  const onLastStep = currentStep === components.length - 1;

  const disabled = canGoNext && !canGoNext();

  const isExitDisabled = isFunction(canSaveAndExit)
    ? disabled || !canSaveAndExit()
    : disabled;

  const Component = components[currentStep]!;

  return (
    <Flex
      flex={1}
      flexDirection="column"
      justifyContent="center"
      gap="l"
      style={style}
    >
      <Container orientation={orientation}>
        <Flex
          flexDirection="column"
          justifyContent="flex-start"
          gap="xxxl"
          alignItems="center"
        >
          <StyledSteps
            orientation={orientation || "horizontal"}
            current={currentStep}
            initial={0}
            items={items}
            status={
              disabled && currentStep !== components.length - 1
                ? "error"
                : "process"
            }
            onChange={(curr) => (disabled ? undefined : onGoToStep(curr))}
          />
          {orientation === "vertical" ? (
            <StepsExtraWrapper>
              <AtiraImage
                width="12rem"
                src={
                  Images[
                    `AtiraSpaceLogoBeta${capitalize(currentTheme)}` as keyof typeof Images
                  ]
                }
              />

              {stepsExtraComponent ? stepsExtraComponent : null}
            </StepsExtraWrapper>
          ) : null}
        </Flex>

        {Component}
      </Container>

      <Flex
        justifyContent="flex-end"
        gap="s"
        className="stepper-buttons-wrapper"
      >
        {!onLastStep && editMode ? (
          <SaveExitButton
            loading={createLoading}
            disabled={isExitDisabled}
            onClick={onCreate}
            // dataTestId={nextButtonDataTestId}
          >
            {t("common.save-exit")}

            <AtiraIcon icon={EntityIcons.Tasks} />
          </SaveExitButton>
        ) : null}

        <Button
          icon={i18n.language === AppLangs.AR ? faAngleRight : faAngleLeft}
          onClick={onPrev}
          disabled={currentStep === 0}
          iconWidth="lg"
          margin="none"
          dataTestId={prevButtonDataTestId}
        >
          {prevButtonLabel}
        </Button>

        {onLastStep ? (
          <CreateButton
            loading={createLoading}
            disabled={disabled}
            onClick={onCreate}
            dataTestId={nextButtonDataTestId}
          >
            {createButtonTitle || t("common.create")}

            <AtiraIcon icon={EntityIcons.Tasks} />
          </CreateButton>
        ) : (
          <NextStepButton
            icon={i18n.language === AppLangs.AR ? faAngleLeft : faAngleRight}
            onClick={onNext}
            disabled={disabled}
            iconWidth="lg"
            dataTestId={nextButtonDataTestId}
          >
            {nextButtonLabel}
          </NextStepButton>
        )}
      </Flex>
    </Flex>
  );
};

type AtiraStepperWithContextProps = {
  components: ReactNode[];
  items: StepsProps["items"];
  style?: React.CSSProperties;
  onSubmit?: VoidFunction;
  fixed?: boolean;
  submitLoading?: boolean;
  submitButtonTitle?: string;
  nextButtonDataTestId?: string;
  prevButtonDataTestId?: string;
  nextButtonLabel?: string;
  prevButtonLabel?: string;
  orientation?: StepsProps["orientation"];
  editMode?: boolean;
  stepsExtraComponent?: ReactNode;
};

export const AtiraStepperWithContext: React.FC<
  AtiraStepperWithContextProps
> = ({
  components,
  items,
  style,
  fixed,
  submitLoading = false,
  onSubmit,
  submitButtonTitle,
  nextButtonDataTestId,
  prevButtonDataTestId,
  nextButtonLabel,
  prevButtonLabel,
  orientation,
  editMode,
  stepsExtraComponent,
}) => {
  return (
    <StepProvider>
      <AtiraStepper
        createButtonTitle={submitButtonTitle}
        onCreate={onSubmit}
        createLoading={submitLoading}
        components={components}
        items={items}
        style={style}
        fixed={fixed}
        nextButtonDataTestId={nextButtonDataTestId}
        prevButtonDataTestId={prevButtonDataTestId}
        nextButtonLabel={nextButtonLabel}
        prevButtonLabel={prevButtonLabel}
        orientation={orientation}
        editMode={editMode}
        stepsExtraComponent={stepsExtraComponent}
      />
    </StepProvider>
  );
};
